import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { faLock, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  CheckboxComponent,
  DropdownComponent,
  TooltipOptions,
} from 'marketpro-design-lib';
import { BehaviorSubject, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { CityRegion, Region } from '../../../models/region-model';
import { RegionSelection } from '../../../models/region-selection.model';
import { SelectorCountry } from '../../../models/selector-country.model';
import { SelectorProvince } from '../../../models/selector-province.model';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import {
  DataFilters,
  DataFiltersService,
} from '../../../services/data-filters.service';
import { UpsaleModalService } from '../../../services/upsale-modal.service';
import { continents, regionsMap } from '../../../../../regions';
import { User } from '../../../../auth/models/user.model';
import {
  CitySearch,
  SelectorCity,
} from '../../../models/region-selection-cities.model';
import { CitiesService } from '../../../services/cities.service';
import { debounceTimer } from 'projects/marketpro-design-lib/src';
import { Permissions } from '../../../../auth/models/permissions.model';
import { TooltipService } from '../../../services/tooltip.service';

@Component({
  selector: 'mpro-country-and-region-selector',
  templateUrl: './country-and-region-selector.component.html',
  styleUrls: [
    './../dropdown-common.scss',
    './country-and-region-selector.component.scss',
  ],
})
export class CountryAndRegionSelectorComponent
  implements OnInit, OnDestroy, OnChanges
{
  addNew: boolean = false;
  public faPlus = faPlus;
  public faLock = faLock;
  public faTrash = faTrash;
  public inputValue: string = '';
  public placeholder: string = '';
  public term: string;
  public inEditRegion: Region;
  public countries: Map<string, SelectorCountry> = new Map();
  public filteredCountries: SelectorCountry[] = [];
  public cityCountries: SelectorCountry[] = [];
  public filteredProvince: SelectorProvince[] = [];
  public cityList: SelectorCity[] = [];
  public userRegions: Region[] = [];
  public dataFilters: DataFilters = new DataFilters();
  public state: 'addNew' | 'default' = 'default';
  public isDeleteModalOpen: boolean = false;
  public regionForDeletion: Region | undefined = undefined;
  public lastHoveredCountry: SelectorCountry;
  public lastHoveredContinent: SelectorProvince | null;
  public indeterminateContinent: SelectorProvince | null;
  private indeterminateContinentList: SelectorProvince[] = [];
  public searchValue: string = '';
  public isCustomRegionSelected: boolean = false;
  public allAsiaCountriesWorkaround = {
    code: 'AS',
    name: 'All Asia',
    authDisabled: false,
    countries: [
      'AFG',
      'ARE',
      'ARM',
      'AZE',
      'BGD',
      'BHR',
      'BRN',
      'BTN',
      'CHN',
      'CYN',
      'CYP',
      'GEO',
      'HKG',
      'IDN',
      'IND',
      'IOA',
      'IRN',
      'IRQ',
      'ISR',
      'JOR',
      'JPN',
      'KAS',
      'KAZ',
      'KGZ',
      'KHM',
      'KOR',
      'KWT',
      'LAO',
      'LBN',
      'LKA',
      'MAC',
      'MMR',
      'MNG',
      'MYS',
      'NPL',
      'OMN',
      'PAK',
      'PHL',
      'PRK',
      'PSE',
      'QAT',
      'SAU',
      'SGP',
      'SYR',
      'THA',
      'TJK',
      'TKM',
      'TLS',
      'TUR',
      'TWN',
      'UZB',
      'VNM',
      'YEM',
    ],
  };
  @Input() public export: boolean = false;
  @Input() public doNotShowSubnational: boolean = false;
  @Input() public allowMultiSelect: boolean = false;
  @Input() public systemRegions: Region[] = [];
  @Input() public systemCountries: SelectorCountry[] = [];
  @Input() public permanentlyOpened: boolean = false;
  @Input() public hideNewRegionButton: boolean = false;
  @Input() public noSubscriptions: boolean = false;
  @Input() public filters: DataFilters = new DataFilters();

  @Output() public selectionChanged: EventEmitter<{
    selection: RegionSelection;
    dataLens?: number;
  }> = new EventEmitter();

  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;
  @ViewChild(CheckboxComponent) public worldCheckbox: CheckboxComponent;
  @HostBinding('style.--cols') public numCols: number = 2;
  @ViewChild('tooltip')
  public tooltip: ElementRef;
  public tooltipOptions: TooltipOptions = {
    type: 'data-explorer',
    interactiveText:
      'You can’t select more cities because you reached the export limit',
  };

  public isCategoriesEnabled = false;
  public isCitiesEnabled = true;
  public citiesPermission = false;
  public loadingCities = false;
  public loadingCitiesCountries = false;
  public loadingCitiesSearch = false;
  private ngUnsubscribe: Subject<void> = new Subject();
  private searchCities$ = new BehaviorSubject<string>('');
  public searchCitiesResult: CitySearch[] = [];
  cityRegions: CityRegion[] = [];
  public isFreemium = false;

  public userOrganizationName: string | undefined = undefined;
  private permissions: Permissions;

  constructor(
    public dataFiltersService: DataFiltersService,
    public authService: AuthService,
    private dashboardService: DashboardStatisticsService,
    private citiesService: CitiesService,
    private cdr: ChangeDetectorRef,
    private upsaleModalService: UpsaleModalService,
    private tooltipService: TooltipService
  ) {}

  public ngOnInit() {
    this.authService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user: User) => {
        this.systemCountries.forEach((c) => this.countries.set(c.code, c));
        this.userOrganizationName = user.data?.organizationName;
        if (this.authService.isLoggedIn()) {
          this.loadRegionCustomFilters();
        }
      });
    this.authService.categoriesEnabled$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (isCategoriesEnabled) =>
          (this.isCategoriesEnabled = isCategoriesEnabled)
      );

    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permissions) => {
        this.permissions = permissions;
        this.isFreemium = permissions.isFreemium;
        this.citiesPermission =
          !!this.permissions?.allowedCountryCities ||
          !!this.permissions?.allowedCities;
        if (this.citiesPermission) {
          this.searchCities();
        }
      });
    this.dashboardService
      .getContinents()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((cityRegions) => {
        this.cityRegions = cityRegions;
      });
    this.getCities();
    this.getCityCountries();
  }

  ngAfterViewInit() {
    if (this.tooltip) {
      this.tooltipService.setTooltipElement('CitiesSelector', this.tooltip);
    }
  }

  private getCities() {
    this.citiesService.getCitiesOnHover$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        debounceTime(debounceTimer),
        switchMap((request) => {
          if (request === undefined) {
            return of([]);
          }
          this.loadingCities = true;
          return this.dashboardService
            .getCities(request.continentCode, request.countryCode)
            .pipe(tap(() => (this.loadingCities = false)));
        })
      )
      .subscribe((cities) => {
        this.citiesService.citiesList$?.next(cities);
        this.cityList = cities;
      });

    this.citiesService.getCities$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap((request) => {
          if (
            request === undefined ||
            this.isCategoriesEnabled ||
            (!this.permissions?.allowedCountryCities?.permissionExtended?.includes(
              request.countryCode
            ) &&
              !this.permissions?.allowedCities?.permissionExtended?.length)
          ) {
            return of([]);
          }

          return this.dashboardService.getCitiesMapList(
            request.continentCode,
            request.countryCode
          );
        })
      )
      .subscribe((citiesMapList) => {
        this.citiesService.citiesMapList$?.next(
          citiesMapList.filter((city) => !!city.boundary)
        );
      });
  }

  private getCityCountries() {
    this.citiesService.getCountries$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((request) => {
          return of(request).pipe(
            switchMap((request) => {
              this.loadingCitiesCountries = true;
              return this.dashboardService.getCountries(request.continent).pipe(
                tap(() => {
                  request.continent.loading = false;
                  this.loadingCitiesCountries = false;
                })
              );
            })
          );
        })
      )
      .subscribe((cityCountries) => {
        if (
          this.permissions?.allowedCountries?.permissionExtended &&
          this.permissions?.allowedCountries?.permissionExtended?.length > 0
        ) {
          this.cityCountries = cityCountries
            .map((country) => {
              return {
                ...country,
                disabled:
                  !this.permissions.allowedCountries?.permissionExtended?.some(
                    (countryCode) => countryCode === country.code
                  ),
              };
            })
            .sort((country) => (country.disabled ? 1 : -1));
          return;
        }
        this.cityCountries = cityCountries;
        this.sortSelected();
      });
  }

  public searchCities() {
    this.searchCities$
      .pipe(
        tap(() => (this.searchCitiesResult = [])),
        takeUntil(this.ngUnsubscribe),
        debounceTime(600),
        distinctUntilChanged(),
        switchMap((query) => {
          this.loadingCitiesSearch = true;
          return query
            ? this.dashboardService
                .searchCities(query)
                .pipe(tap(() => (this.loadingCitiesSearch = false)))
            : [];
        })
      )
      .subscribe((cities) => (this.searchCitiesResult = cities));
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && changes.filters.currentValue) {
      this.onFilterChanges(changes.filters.currentValue);
    }
  }

  public onFilterChanges(filters: DataFilters) {
    this.dataFilters = filters;
    if (this.dataFilters.regionSelection.countries.size === 0) {
      this.dataFilters.regionSelection.world = true;
      this.indeterminateContinentList = [];
    }
    if (
      this.isCitiesEnabled &&
      !!this.dataFilters.regionSelection.countries.size
    ) {
      if (this.allowMultiSelect) {
        const regions = continents.filter((continent) => {
          return continent.countries.some((country) => {
            const t = Array.from(
              this.dataFilters.regionSelection.countries.entries()
            ).some(([_, filterCountry]) => {
              return filterCountry.code === country;
            });
            return t;
          });
        });
        this.indeterminateContinentList = regions;
      }
      const region = continents.find((continent) =>
        continent.countries.find(
          (country) =>
            country ===
            this.dataFilters.regionSelection.countries.entries().next()
              .value?.[1].code
        )
      );
      if (region) {
        const indeterminateContinentTemp = new Region(
          region?.name,
          region?.code
        );
        this.indeterminateContinent = indeterminateContinentTemp;
      }
    }
    if (!this.dataFilters.dataLens) {
      this.dataFilters.regionSelection.subnational.clear();
    }
    if (
      !this.doNotShowSubnational &&
      this.dataFilters.regionSelection.countries.size == 1 &&
      [...this.dataFilters.regionSelection.countries.values()][0].subnational
        ?.length
    ) {
      this.numCols = 3;
    } else {
      this.numCols = this.dataFilters.dataLens ? 3 : 2;
    }
    this.sortSelected();
  }

  public areCitiesDisabled(): boolean {
    return this.dataFilters.regionSelection.cities.size >= 50;
  }

  public isRegionChecked(r: Region | CityRegion) {
    if (this.dataFilters.regionSelection.world && this.allowMultiSelect) {
      return true;
    }
    if (this.dataFilters.regionSelection.regions.has(r.name)) {
      return true;
    }
    if (this.isRegionIndeterminate(r)) {
      return true;
    }
    return false;
  }

  onSearchedCitySelected(c: CitySearch, isChecked: boolean) {
    const currentSelection = this.dataFilters.regionSelection;
    const region = new Region(c.continentName, c.continentCode);
    const country = new SelectorCountry(c.countryCode, c.countryName);
    const city = new SelectorCity(c.cityId);
    this.indeterminateContinent = region;
    if (!currentSelection.countries.has(country.code)) {
      currentSelection.toggleCountry(country);
    }
    currentSelection.toggleCity(city);
    this.citiesService.getCountries$.next({ continent: region as Region });
    this.citiesService.getCities$.next({
      continentCode: region.code,
      countryCode: country.code,
    });
    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
    });
    this.sortSelected();
    this.cdr.detectChanges();
  }

  onCitiesRegionSelected(region: any, isChecked: boolean) {
    if (
      this.permissions.allowedCountries?.permissionExtended &&
      this.permissions.allowedCountries.permissionExtended?.length > 0 &&
      this.isContinentAllowed(region)
    ) {
      this.onCountrySelected(this.cityCountries[0], isChecked);
      return;
    }
    const currentSelection = this.dataFilters.regionSelection;
    currentSelection.toggleCity();
    this.citiesService.getCities$.next(undefined);
    this.citiesService.getCitiesOnHover$.next(undefined);
    const countries = new Map<string, SelectorCountry>();
    this.cityCountries.forEach((c) => countries.set(c.code, c));
    let countrySelected: SelectorCountry | null = null;
    for (const [_, value] of currentSelection.countries) {
      if (value.region === region.code) {
        countrySelected = value;
      }
    }
    if (!this.isFreemium) {
      if (countrySelected !== null && !this.allowMultiSelect) {
        currentSelection.toggleCountry(countrySelected, this.allowMultiSelect);
      } else {
        currentSelection.toggleRegion(
          region,
          countries,
          this.allowMultiSelect,
          this.isCitiesEnabled
        );
      }
    } else {
      currentSelection.setFreemiumRegion(region);
    }

    this.indeterminateContinent = null;
    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
    });
    this.cdr.detectChanges();
  }

  public displayCitiesList() {
    return (
      !this.isCategoriesEnabled &&
      this.isCitiesEnabled &&
      this.searchValue !== '' &&
      this.dataFilters.regionSelection.countries.size
    );
  }

  public onRegionSelected(
    region: Region,
    isCustom: boolean,
    isChecked: boolean
  ) {
    this.term = '';
    this.isCustomRegionSelected = (region.id ? true : false) && isChecked;
    if (isCustom && this.isCitiesEnabled) {
      this.citiesService.getCities$.next(undefined);
      this.citiesService.getCitiesOnHover$.next(undefined);
    }
    const currentSelection = this.dataFilters.regionSelection;
    let countrySelected = this.isCountryFromTheContinentSelected(
      region,
      isCustom
    );
    if (countrySelected && !this.allowMultiSelect) {
      currentSelection.toggleCountry(countrySelected);
    } else {
      currentSelection.toggleRegion(
        region,
        this.countries,
        this.allowMultiSelect
      );
    }

    if (!isChecked) {
      currentSelection.toggleCity();
      this.citiesService.getCities$.next(undefined);
      this.citiesService.getCitiesOnHover$.next(undefined);
    }

    if (!currentSelection.regions.size) {
      this.numCols = 2;
    }

    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
    });
    this.cdr.detectChanges();
  }

  public preventClick(event: MouseEvent) {
    if (!this.authService.isLoggedIn() || this.isFreemium) {
      event.preventDefault();
    }
  }

  public isCountryFromTheContinentSelected(
    region: Region,
    isCustom = false
  ): any {
    let regionCountriesLength =
      regionsMap.get(region.code)?.countries.length || 0;
    if (isCustom) {
      for (const country of this.dataFilters?.regionSelection.countries.values()) {
        for (let j = 0; j < region.countries.length; j++) {
          if (country.code === region.countries[j]) {
            return country;
          }
        }
      }
    } else {
      for (const country of this.dataFilters?.regionSelection.countries.values()) {
        for (let j = 0; j < regionCountriesLength; j++) {
          if (country.code === regionsMap.get(region.code)?.countries[j]) {
            return country;
          }
        }
      }
    }
    return false;
  }

  public isRegionIndeterminate(
    region: Region | CityRegion,
    isCustomRegion = false
  ): boolean {
    if ((region as Region)?.countries?.length) {
      let regionCountriesLength =
        regionsMap.get(region.code)?.countries.length || 0;

      if (isCustomRegion) {
        for (const country of this.dataFilters?.regionSelection.countries.values()) {
          for (let j = 0; j < (region as Region).countries.length; j++) {
            if (country.code === (region as Region).countries[j]) {
              return true;
            }
          }
        }
      } else {
        for (const country of this.dataFilters?.regionSelection.countries.values()) {
          for (let j = 0; j < regionCountriesLength; j++) {
            if (country.code === regionsMap.get(region.code)?.countries[j]) {
              return true;
            }
          }
        }
      }
    } else {
      if (this.allowMultiSelect) {
        return !!this.indeterminateContinentList.find(
          (cont) => cont.name === region.name
        );
      }
      return (
        this.indeterminateContinent?.code === region.code &&
        !!this.dataFilters?.regionSelection?.countries?.size
      );
    }
    return false;
  }

  public getCountries(): SelectorCountry[] {
    if (this.isCustomRegionSelected) {
      if (this.isCitiesEnabled) {
        return this.filteredCountries;
      }
      let selectedCountries: SelectorCountry[] = [];
      for (const country of this.dataFilters?.regionSelection.countries.values()) {
        selectedCountries.push(country);
      }
      return this.filteredCountries;
    } else {
      return this.isCitiesEnabled ? this.cityCountries : this.filteredCountries;
    }
  }

  public onCountrySelected(country: SelectorCountry, isChecked: boolean) {
    if (this.isFreemium) {
      this.authService.openFreemiumRequestModal.next({
        isOpen: true,
        requestCategory: `country ${country.name}`,
      });
      return;
    }
    if (!isChecked) {
      this.citiesService.getCities$.next(undefined);
      this.citiesService.getCitiesOnHover$.next(undefined);
    } else if (this.isCitiesEnabled) {
      const continentCode =
        this.lastHoveredContinent?.code || this.indeterminateContinent?.code;
      continentCode &&
        this.citiesService.getCities$.next({
          continentCode,
          countryCode: country.code,
        });
    }

    if (country.code === 'AS') {
      this.onRegionSelected(this.allAsiaCountriesWorkaround, false, true);
    } else {
      if (!this.authService.isLoggedIn()) {
        this.upsaleModalService.open();
        return;
      }
      const region = this.systemRegions.find((region) =>
        region.countries.some((_country) => _country === country.code)
      );
      country.region = region?.code;
      if (this.isCitiesEnabled && region) {
        this.indeterminateContinent = region;
        this.citiesService.getCountries$.next({
          continent: region,
        });
      }
      const currentSelection = this.dataFilters.regionSelection;
      currentSelection.toggleCountry(country, this.allowMultiSelect);
      currentSelection.toggleCity();

      if (currentSelection.countries.size === 1 && !this.doNotShowSubnational) {
        const [selectedCountry] = currentSelection.countries.values();
        if (selectedCountry.subnational && selectedCountry.subnational.length) {
          this.numCols = 3;
        }
      } else if (
        currentSelection.countries.size === 0 &&
        this.dataFilters.dataLens
      ) {
        this.onSubnationalToggleChange(false);
      } else if (!this.dataFilters.dataLens) {
        this.numCols = 2;
      }
      if (this.lastHoveredContinent) {
        this.indeterminateContinent = this.lastHoveredContinent;
      }

      this.lastHoveredContinent = null;
      this.dropdown.focusInput();
      this.cdr.detectChanges();
      this.selectionChanged.emit({ selection: currentSelection });
    }
  }

  public async onProvinceSelected(
    province: SelectorProvince,
    isChecked: boolean
  ) {
    if (isChecked) {
      this.onCountrySelected(this.lastHoveredCountry, false);
      this.blockAllExceptSubnational(province);
    } else {
      this.allowAllOptions();
    }
  }

  public blockAllExceptSubnational(province) {
    const currentSelection = this.dataFilters.regionSelection;
    currentSelection.toggleProvince(province, this.allowMultiSelect);
    this.selectionChanged.emit({ selection: currentSelection, dataLens: 1 });
    setTimeout(() => this.cdr.markForCheck());
  }

  public allowAllOptions() {
    this.dataFilters.regionSelection.subnational.clear();
    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
      dataLens: 0,
    });
    setTimeout(() => this.cdr.markForCheck());
  }

  public onSubnationalToggleChange(state: boolean) {
    const dataLens = state ? 1 : 0;
    if (!dataLens && this.dataFilters.regionSelection.countries.size === 0) {
      this.numCols = 2;
    }
    if (!dataLens) {
      this.dataFilters.regionSelection.subnational.clear();
    }

    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
      dataLens: dataLens,
    });
    setTimeout(() => this.cdr.markForCheck());
  }

  public shouldFilterCountryItem(c: SelectorCountry) {
    if (!this.term) {
      return true;
    }

    if (c.name.toLowerCase().includes(this.term)) {
      return true;
    }

    if (
      c.subnational &&
      c.subnational.filter((p) => p.name.toLowerCase().includes(this.term))
        .length
    ) {
      return true;
    }
    return false;
  }

  public reset() {
    this.numCols = 2;
    setTimeout(() => this.cdr.markForCheck());
  }

  public getRegions() {
    return this.isCitiesEnabled ? this.cityRegions : this.systemRegions;
  }

  public clearSelection() {
    this.selectionChanged.emit({
      selection: new RegionSelection(),
      dataLens: 0,
    });
    this.citiesService.getCities$.next(undefined);
    this.citiesService.getCitiesOnHover$.next(undefined);

    this.reset();
  }

  setState(state: 'addNew' | 'default') {
    if (!this.authService.isLoggedIn() && state === 'addNew') {
      this.upsaleModalService.open();
      return;
    }
    this.inEditRegion = new Region('');
    this.state = state;
  }

  search(val: string) {
    this.lastHoveredContinent = null;
    if (!val) {
      if (!this.dataFilters?.regionSelection?.countries.size) {
        this.filteredCountries = [];
      } else {
        const countriesArr: SelectorCountry[] = [];
        const region = this.systemRegions.find((region) =>
          region.countries.some(
            (_country) =>
              _country ===
              this.dataFilters?.regionSelection?.countries.entries().next()
                .value?.[1].code
          )
        );
        region && this.findCountries(region, countriesArr);
        this.filteredCountries = countriesArr;
      }
      this.searchValue = '';
      this.searchCities$.next('');
      return;
    }
    this.searchCities$.next(val);
    this.filteredCountries = this.systemCountries.filter((country) => {
      const indexOfSubstr = country.name
        .toLocaleLowerCase()
        .indexOf(val.toLocaleLowerCase());
      return indexOfSubstr !== -1 ? true : false;
    });
    this.sortSelected();
  }

  public dropdownStateChange(open: boolean) {
    if (!open) {
      this.search('');
    }
    this.lastHoveredContinent = null;
  }

  onSave(regionGroup: Region) {
    this.dashboardService
      .saveRegionCustomFilter(regionGroup)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.closeCreateWindowAndReloadGroups());
  }

  loadRegionCustomFilters() {
    this.dashboardService
      .loadRegionCustomFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((regionFilters) => {
        this.userRegions = regionFilters.map(
          (regionFilter) =>
            new Region(
              regionFilter.name,
              regionFilter.name,
              regionFilter.countries,
              regionFilter.id,
              false
            )
        );
      });
  }

  private sorting(
    val1: string,
    val2: string,
    list: Map<string, SelectorCity | SelectorCountry>
  ) {
    if (list?.has(val1) && list?.has(val2)) {
      return val1.localeCompare(val2);
    }

    if (list?.has(val1)) {
      return -1;
    }

    if (list?.has(val2)) {
      return 1;
    }

    return val1.localeCompare(val2);
  }

  // sort selected by name first and then unselected by name
  private sortSelected() {
    if (this.cityList) {
      this.cityList.sort((c1, c2) =>
        this.sorting(
          c1.name,
          c2.name,
          this.dataFilters?.regionSelection?.cities
        )
      );
    }
    this.filteredCountries.sort((c1, c2) =>
      this.sorting(
        c1.code,
        c2.code,
        this.dataFilters?.regionSelection?.countries
      )
    );
    this.cityCountries.sort((c1, c2) =>
      this.sorting(
        c1.code,
        c2.code,
        this.dataFilters?.regionSelection?.countries
      )
    );
  }

  public onEditClicked(region: Region) {
    this.setState('addNew');
    this.inEditRegion = region;
  }

  private closeCreateWindowAndReloadGroups() {
    this.setState('default');
    this.loadRegionCustomFilters();
  }

  public onDeleteClicked(region: Region) {
    if (region.id === undefined) {
      return;
    }

    this.regionForDeletion = region;
    this.isDeleteModalOpen = true;
  }

  public onDelete() {
    if (!this.regionForDeletion || !this.regionForDeletion.id) {
      return;
    }

    this.isDeleteModalOpen = false;
    this.dashboardService
      .deleteRegionCustomFilter(this.regionForDeletion.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.regionForDeletion = undefined;
        this.loadRegionCustomFilters();
      });
  }

  public getInputString(): string {
    if (this.permanentlyOpened) {
      return '';
    }

    return this.dataFilters.regionSelection.getName(5);
  }

  public getCityCount(c: SelectorCountry): string {
    return c.subTerritoriesCount ? '(' + c.subTerritoriesCount + ')' : '';
  }

  public onCitySelected(c, e) {
    this.dataFiltersService.setDataFilters({
      ...this.dataFilters,
      generation: undefined,
    });

    if (this.isFreemium) {
      this.authService.openFreemiumRequestModal.next({
        isOpen: true,
        requestCategory: `city ${c.name}`,
      });
      return;
    }
    if (!this.authService.isLoggedIn()) {
      this.upsaleModalService.open();
      return;
    }
    const currentSelection = this.dataFilters.regionSelection;
    currentSelection.toggleCity(c, this.lastHoveredCountry, this.export);

    if (!this.export) {
      this.citiesService.getCities$.next({
        continentCode:
          this.indeterminateContinent?.code || this.lastHoveredContinent!.code,
        countryCode: this.lastHoveredCountry.code,
      });
    }

    this.sortSelected();
    this.dropdown.focusInput();
    this.cdr.detectChanges();
    this.selectionChanged.emit({ selection: currentSelection });
  }

  public onContinentsHovered(g, isCustomRegion, ev) {
    this.isCustomRegionSelected = isCustomRegion;
    if (this.isCitiesEnabled && !isCustomRegion) {
      if (
        this.lastHoveredContinent === null ||
        this.lastHoveredContinent?.code !== g.code
      ) {
        this.citiesService.getCountries$.next({ continent: g });
        this.lastHoveredContinent = g;
      }
      this.citiesService.getCitiesOnHover$.next(undefined);
      return;
    }
    let countriesArr: SelectorCountry[] = [];
    if (g.code === 'AS') {
      countriesArr.push(this.allAsiaCountriesWorkaround);
    }
    this.findCountries(g, countriesArr);
    this.lastHoveredContinent = g;
    this.filteredCountries = countriesArr;
  }

  private findCountries(g, countriesArr) {
    g.countries.forEach((countryCode) => {
      let countryObj = this.systemCountries.find((x) => x.code === countryCode);
      if (countryObj) {
        countriesArr.push(countryObj);
      }
    });
  }

  public onWorldSelected() {
    //Keep checkbox selected on change
    this.worldCheckbox.writeValue(true);
    this.dataFilters.regionSelection.toggleWorld();

    this.selectionChanged.emit({
      selection: this.dataFilters.regionSelection,
    });
  }

  public onCountriesHovered(c, ev) {
    this.filteredProvince = c.subnational;
    const region = this.lastHoveredContinent?.code;
    this.lastHoveredCountry = { ...c, region };
    if (!c.subTerritoriesCount) {
      this.citiesService.getCitiesOnHover$.next(undefined);
      return;
    }
    if (this.isCitiesEnabled) {
      const continentCode =
        this.lastHoveredContinent?.code || this.indeterminateContinent?.code;
      continentCode &&
        this.citiesService.getCitiesOnHover$.next({
          continentCode,
          countryCode: c.code,
        });
      return;
    }
  }

  public onMouseEnter(ev: MouseEvent) {
    if (this.areCitiesDisabled()) {
      this.tooltipService.showTooltip(ev, 'CitiesSelector');
    }
  }

  public onMouseLeave() {
    this.tooltipService.hideTooltip('CitiesSelector');
  }

  public closeCitiesContainer() {
    if (this.export) {
      this.cityList = [];
    }
  }

  public isContinentAllowed(continent: CityRegion) {
    if (this.userOrganizationName === 'MGas') {
      return continent.name === 'Africa';
    }
    return true;
  }

  setAddNew(state: boolean) {
    if (!this.authService.isLoggedIn() && state) {
      this.upsaleModalService.open();
      return;
    }
    this.addNew = state;
  }

  public toggleDropdown() {
    this.dropdown.toggleDropdown();
  }
}
